import FormAlert from "./FormAlert";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { LoginError, loginWithPassword } from "@/common/auth";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { Button } from "../button";
import { Field, FieldLabel } from "../field";
import { Input } from "../input";
import PageContainer from "./PageContainer";

interface LoginFormValues {
  username: string;
  password: string;
}

export default function PasswordLogin() {
  const navigate = useNavigate();
  const me = useAppSelector(selectMe);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginFormValues>({
    defaultValues: { username: "", password: "" },
  });

  // Already signed in -- nothing to do here.
  useEffect(() => {
    if (me) navigate("/", { replace: true });
  }, [me, navigate]);

  const onSubmit = async (values: LoginFormValues) => {
    setError(null);
    try {
      await loginWithPassword(values.username, values.password);
      navigate("/");
    } catch (e) {
      setError(
        e instanceof LoginError
          ? e.message
          : "Something went wrong. Please try again.",
      );
      form.setValue("password", "");
    }
  };

  return (
    <PageContainer>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="max-w-sm mx-auto mt-16 flex flex-col gap-4"
      >
        <h1
          className="text-[18px] font-semibold text-foreground"
          style={{ fontFamily: "var(--font-serif)" }}
        >
          Sign in
        </h1>

        <Controller
          name="username"
          control={form.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="loginUsername">Username</FieldLabel>
              <Input
                {...field}
                id="loginUsername"
                type="text"
                autoComplete="username"
                autoFocus
              />
            </Field>
          )}
        />

        <Controller
          name="password"
          control={form.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="loginPassword">Password</FieldLabel>
              <Input
                {...field}
                id="loginPassword"
                type="password"
                autoComplete="current-password"
              />
            </Field>
          )}
        />

        <FormAlert error={error} />

        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </PageContainer>
  );
}
